import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import DraggablePanel from './DraggablePanel';
import { useScenePerf } from '../../hooks/useScenePerf';
import { useLoadingTracker } from '../../contexts/LoadingTrackerContext';
import { formatBytes } from '../../Utils/fetchWithProgress';
import { formatCount, formatFps, formatMs } from '../../Utils/scenePerf';

function fpsTone(fps) {
  if (fps >= 50) return 'text-emerald-400';
  if (fps >= 30) return 'text-yellow-400';
  return 'text-red-400';
}

function heapTone(heap) {
  if (!heap?.limit) return 'text-zinc-200';
  const ratio = heap.used / heap.limit;
  if (ratio >= 0.8) return 'text-red-400';
  if (ratio >= 0.6) return 'text-yellow-400';
  return 'text-emerald-400';
}

function kindLabel(kind) {
  if (kind === 'points') return 'pts';
  if (kind === 'instanced') return 'inst';
  if (kind === 'lines') return 'ln';
  if (kind === 'mesh') return 'tri';
  return kind;
}

function Metric({ label, value, tone, hint }) {
  return (
    <div className="min-w-0 rounded-sm border border-yellow-600/20 bg-black/40 px-2 py-1.5">
      <p className="text-[8px] uppercase tracking-[0.14em] text-zinc-500">{label}</p>
      <p className={`mt-0.5 text-[13px] font-semibold tabular-nums leading-none ${tone || 'text-zinc-100'}`}>
        {value}
      </p>
      {hint ? <p className="mt-1 text-[9px] text-zinc-500 leading-snug">{hint}</p> : null}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-3 first:mt-0">
      <h3 className="mb-1.5 text-[8px] font-semibold uppercase tracking-[0.16em] text-yellow-600/80">{title}</h3>
      {children}
    </section>
  );
}

function FpsSpark({ history }) {
  if (!history?.length) return null;
  const max = Math.max(60, ...history);
  return (
    <div className="mt-2 flex h-8 items-end gap-px" title="FPS over the last ~16 seconds">
      {history.map((v, i) => {
        const h = Math.max(2, Math.round((v / max) * 32));
        const bg = v >= 50 ? 'bg-emerald-500' : v >= 30 ? 'bg-yellow-500' : 'bg-red-500';
        return (
          <span
            key={i}
            className={`w-full min-w-[2px] rounded-t-sm ${bg} opacity-80`}
            style={{ height: h }}
          />
        );
      })}
    </div>
  );
}

export default function SceneDataPanel({ three, layerVisibility, onClose }) {
  const snap = useScenePerf({ enabled: true, three, layerVisibility });
  const { getLoadingList, getFailedList, loadingProgress, getLoadingCount, getFailedCount } =
    useLoadingTracker();

  const loadingList = getLoadingList();
  const failedList = getFailedList();
  const loadingCount = getLoadingCount();
  const failedCount = getFailedCount();

  const heapHint = useMemo(() => {
    if (!snap.heap) return 'JS heap is Chrome/Edge only';
    const pct = Math.round((snap.heap.used / snap.heap.limit) * 100);
    return `${pct}% of ${formatBytes(snap.heap.limit)} limit`;
  }, [snap.heap]);

  return createPortal(
    <DraggablePanel
      id="scene-data"
      handleLabel="Scene data"
      zIndex={1320}
      resizable
      defaultSize={{ width: 320, height: 520 }}
      minWidth={280}
      minHeight={280}
      className="pointer-events-auto"
    >
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-b-md border border-t-0 border-yellow-600/35 bg-zinc-950/95 shadow-[0_12px_36px_rgba(0,0,0,0.55)] backdrop-blur-md">
        <div className="flex items-start justify-between gap-2 border-b border-yellow-600/20 px-2.5 py-2">
          <p className="text-[10px] leading-snug text-zinc-400">
            Live renderer load as layers open. Hidden models drop off the GPU; cached CSV stays in JS memory.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="m-0 h-6 w-6 shrink-0 rounded-sm border border-yellow-600/30 text-yellow-500 hover:border-yellow-400 hover:bg-zinc-900"
            title="Close scene data"
            aria-label="Close scene data"
          >
            ×
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2.5 py-2">
          <Section title="Frame">
            <div className="grid grid-cols-2 gap-1.5">
              <Metric
                label="FPS"
                value={formatFps(snap.fps)}
                tone={fpsTone(snap.fps)}
                hint={snap.ready ? 'Scene render loop' : 'Waiting for canvas'}
              />
              <Metric label="Frame time" value={formatMs(snap.frameMs)} />
            </div>
            <FpsSpark history={snap.fpsHistory} />
          </Section>

          <Section title="Renderer">
            <div className="grid grid-cols-2 gap-1.5">
              <Metric label="Draw calls" value={formatCount(snap.calls)} />
              <Metric label="Triangles" value={formatCount(snap.triangles)} />
              <Metric label="GPU points" value={formatCount(snap.renderPoints)} />
              <Metric label="GPU lines" value={formatCount(snap.renderLines)} />
              <Metric label="Geometries" value={formatCount(snap.geometries)} />
              <Metric label="Textures" value={formatCount(snap.textures)} />
              <Metric label="Programs" value={formatCount(snap.programs)} />
              <Metric
                label="GPU buffers"
                value={formatBytes(snap.gpuBytes)}
                hint="Typed arrays on loaded geometries"
              />
            </div>
          </Section>

          <Section title="Memory">
            <div className="grid grid-cols-2 gap-1.5">
              <Metric
                label="JS heap"
                value={snap.heap ? formatBytes(snap.heap.used) : 'n/a'}
                tone={heapTone(snap.heap)}
                hint={heapHint}
              />
              <Metric
                label="Device RAM"
                value={snap.deviceMemory ? `${snap.deviceMemory} GB` : 'n/a'}
                hint="navigator.deviceMemory"
              />
            </div>
          </Section>

          <Section title="Layers">
            <div className="grid grid-cols-2 gap-1.5">
              <Metric label="Visible" value={`${snap.layers.visible} / ${snap.layers.catalogue}`} />
              <Metric label="Ready (hidden)" value={formatCount(snap.layers.hiddenReady)} />
              <Metric label="Loading" value={formatCount(Math.max(snap.layers.loading, loadingCount))} />
              <Metric label="Failed" value={formatCount(Math.max(snap.layers.failed, failedCount))} />
            </div>
            {(loadingList.length > 0 || failedList.length > 0) && (
              <ul className="mt-2 space-y-1">
                {loadingList.map((name) => {
                  const progress = loadingProgress.get(name);
                  const pct = progress?.indeterminate ? null : progress?.percent;
                  return (
                    <li key={`load-${name}`} className="text-[10px] text-yellow-300/90">
                      Loading {name}
                      {pct != null ? ` · ${Math.round(pct)}%` : progress?.phase ? ` · ${progress.phase}` : ''}
                    </li>
                  );
                })}
                {failedList.map((row) => (
                  <li key={`fail-${row.name}`} className="text-[10px] text-red-400">
                    Failed {row.name}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Scene graph">
            <div className="grid grid-cols-2 gap-1.5">
              <Metric label="Objects" value={`${formatCount(snap.visibleObjects)} / ${formatCount(snap.objects)}`} hint="Visible / total" />
              <Metric label="Meshes" value={formatCount(snap.meshes)} />
              <Metric label="Point clouds" value={formatCount(snap.points)} hint={`${formatCount(snap.pointCount)} cells`} />
              <Metric label="Instanced" value={formatCount(snap.instanced)} hint={`${formatCount(snap.instances)} instances`} />
            </div>
          </Section>

          <Section title="Open models">
            {snap.layerRows.length === 0 ? (
              <p className="px-1 py-2 text-center text-[11px] text-zinc-500">
                No GPU geometry yet. Turn on a layer to see its cost here.
              </p>
            ) : (
              <ul className="divide-y divide-yellow-600/10">
                {snap.layerRows.map((row) => (
                  <li key={row.name} className="flex items-baseline justify-between gap-2 py-1">
                    <span className="min-w-0 truncate text-[11px] text-zinc-200" title={row.name}>
                      {row.name}
                    </span>
                    <span className="shrink-0 tabular-nums text-[10px] text-zinc-400">
                      {formatCount(row.count)} {kindLabel(row.kind)}
                      <span className="ml-1.5 text-zinc-600">{formatBytes(row.bytes)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </DraggablePanel>,
    document.body
  );
}
